import { DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import Bowser from 'bowser';
import { PLATFORMS, QUERY_PARAMS } from './constants';
import useQuery from './useQuery';

const fallbackPlatform = PLATFORMS.WEB;

export enum EnvironmentEnum {
  PRODUCTION = 'PRODUCTION',
  STAGE = 'STAGE',
  LOCAL = 'LOCAL'
}

export type UserAgent = {
  orientation: OrientationTypeEnum;
  deviceType: DeviceTypeEnum;
};

type Mobile = {
  isMobile: boolean;
};

export type UserAgentWithMobile = UserAgent & Mobile;

// derived from Bowser#PLATFORMS_MAP
export enum DeviceTypeEnum {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
  Tv = 'tv'
}

export enum OrientationTypeEnum {
  Landscape = 'landscape',
  Portrait = 'portrait'
}

export const getEnvironment = () => {
  return import.meta.env.VITE_ENVIRONMENT || EnvironmentEnum.LOCAL;
};

export const isLocalEnvironment = (): boolean => {
  return getEnvironment() === EnvironmentEnum.LOCAL;
};

export const isProduction = (): boolean => {
  return getEnvironment() === EnvironmentEnum.PRODUCTION;
};

const isCteEnv = (): boolean => {
  return getEnvironment() === EnvironmentEnum.STAGE;
};

export const getDexfSegment = (): DexfSettingsFilterSegmentEnum => {
  const environment = getEnvironment();

  switch (environment) {
    case EnvironmentEnum.PRODUCTION: {
      return DexfSettingsFilterSegmentEnum.production;
    }
    case EnvironmentEnum.STAGE: {
      return DexfSettingsFilterSegmentEnum.verification;
    }
    default: {
      return DexfSettingsFilterSegmentEnum.development;
    }
  }
};

export const getAppName = (): string => {
  return import.meta.env.VITE_NPM_PACKAGE_NAME || 'skytta';
};

export const getAppVersion = (): string => {
  return import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_NPM_PACKAGE_VERSION || '0.0.1';
};

/**
 * Usually used to disable things if tests is running, also redux isn't working well with unit tests so this check could solve that.
 */
export const isRunningTest = import.meta.env.VITEST_WORKER_ID !== undefined;

export const isE2eTest = (): boolean => {
  return !!window.Cypress || import.meta.env.CYPRESS;
};

export const isE2eOrUnitTest = (): boolean => {
  return isE2eTest() || isRunningTest;
};

export const getAddonAppPath = (): string => {
  return isProduction() || isCteEnv()
    ? `${window.location.origin}/addon-app`
    : 'https://www.ikea.com/addon-app';
};

export const getNonVersionedAssetsPath = (): string => {
  return `${getAddonAppPath()}/skytta/assets`;
};

export const getPlatformFromPath = (): string | null => {
  const match = window.location.pathname.match(/\/(web|kiosk)\//);
  return match?.[1] || null;
};

export const getPlatform = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  const platform = searchParams.get(QUERY_PARAMS.UI_PLATFORM) || getPlatformFromPath();

  if (platform) {
    return platform;
  }

  return fallbackPlatform;
};

export const isKiosk = (): boolean => {
  return getPlatform().toLocaleLowerCase() === PLATFORMS.KIOSK;
};

export const isForcedProduction = (): boolean => {
  return useQuery(QUERY_PARAMS.FORCE_PRODUCTION) === 'true';
};

const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const isUpptacka = (): boolean => {
  return useQuery(QUERY_PARAMS.ORIGIN)?.toLocaleLowerCase() === 'upptacka' && isKiosk();
};

export const isIpexGallery = (): boolean => {
  return useQuery(QUERY_PARAMS.IPEX_GALLERY)?.toLocaleLowerCase() === 'true' && isKiosk();
};

export const isIpexGalleryOrUpptacka = (): boolean => {
  return isIpexGallery() || isUpptacka();
};

/**
 * https://i-p-e-x.atlassian.net/wiki/spaces/KP/pages/36559355999/Planner+implementation
 * Upptäcka and IPEX Gallery can be implemented in the same way with the same utility. By selecting a different platform or letting the utility handle the state it self.
 *
 * Good to know
 * There is no need for a screensaver from the planner application
 * There is no need for a countdown banner from the planner application
 * There is no need for a virtual keyboard from the planner application
 */
export const useIdleKioskHandler = (): boolean => {
  return isKiosk() && !isIpexGalleryOrUpptacka();
};

export const showLanguageSelector = (): boolean => {
  return isKiosk() && !isUpptacka();
};

export const usesVirtualKeyboard = (): boolean => {
  return (
    useQuery(QUERY_PARAMS.ENABLE_VIRTUAL_KEYBOARD) === 'true' ||
    (isTouchDevice() && isKiosk() && !isUpptacka())
  );
};

export const isLighthouse = () => {
  return useQuery(QUERY_PARAMS.LIGHTHOUSE)?.toLocaleLowerCase() === 'true';
};

export const getIdleTime = (): number => {
  const idleTimeString = useQuery(QUERY_PARAMS.IDLE_TIME);
  if (idleTimeString !== null) {
    const idleTime = parseInt(idleTimeString, 10);
    return idleTime;
  }
  return 60;
};

export const getDeviceType = (): DeviceTypeEnum => {
  return <DeviceTypeEnum>Bowser.getParser(window.navigator.userAgent).getPlatformType(true);
};

export const isIpad = () => {
  return (
    getDeviceType() === DeviceTypeEnum.Tablet ||
    /iPad/.test(navigator.userAgent) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const getOrientation = (): OrientationTypeEnum => {
  return window.innerWidth > window.innerHeight
    ? OrientationTypeEnum.Landscape
    : OrientationTypeEnum.Portrait;
};

export const getUserAgent = () => {
  const orientation = getOrientation();
  const deviceType = getDeviceType();

  return { orientation, deviceType };
};
